import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api, setToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import i18n from "i18next";
import { ShieldCheck, Loader2, AlertCircle, UserCheck, Info } from "lucide-react";

export default function AcceptInvite() {
    const [params] = useSearchParams();
    const nav = useNavigate();
    const { refreshWorkspace } = useAuth();
    const token = params.get("token");
    const [info, setInfo] = useState(null);
    const [error, setError] = useState(null);
    const [name, setName] = useState("");
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!token) { setError(i18n.t("invite.no_token")); return; }
        api.get(`/team/invites/info?token=${encodeURIComponent(token)}`)
            .then((r) => { setInfo(r.data); setName(r.data.existing_user_name || r.data.name || ""); })
            .catch((e) => setError(e?.response?.data?.detail || i18n.t("invite.invalid")));
    }, [token]);

    const isExisting = !!info?.user_exists;
    const alreadyMember = !!info?.already_member;

    const submit = async (e) => {
        e.preventDefault();
        if (!isExisting) {
            if (password.length < 6) { toast.error(i18n.t("invite.pw_min")); return; }
            if (password !== confirm) { toast.error(i18n.t("invite.pw_mismatch")); return; }
        } else if (!password) {
            toast.error(i18n.t("invite.pw_existing_required"));
            return;
        }
        setSaving(true);
        try {
            const payload = isExisting
                ? { token, password }
                : { token, password, name };
            const { data } = await api.post("/team/invites/accept", payload);
            setToken(data.token);
            await refreshWorkspace?.();
            toast.success(i18n.t("invite.welcome_toast", { name: data.workspace.name }));
            nav("/app/dashboard");
            setTimeout(() => window.location.reload(), 200);
        } catch (err) {
            toast.error(err?.response?.data?.detail || i18n.t("common.failed"));
        } finally { setSaving(false); }
    };

    return (
        <div className="min-h-screen bg-grain grid place-items-center p-6">
            <div className="w-full max-w-md bg-[var(--jr-surface)] border border-white/10 rounded-2xl p-7 jr-fadein"
                data-testid="accept-invite-card">
                <div className="flex items-center gap-2 mb-1">
                    <div className="w-8 h-8 rounded-full bg-[var(--jr-brand)]/15 grid place-items-center"><ShieldCheck size={16} className="text-[var(--jr-brand)]" /></div>
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">Join workspace</span>
                </div>

                {error ? (
                    <div className="mt-4 flex items-start gap-3 rounded-lg border border-[var(--jr-danger)]/30 bg-[var(--jr-danger)]/10 p-3">
                        <AlertCircle size={16} className="text-[var(--jr-danger)] mt-0.5 shrink-0" />
                        <div className="text-sm text-white">{error}</div>
                    </div>
                ) : !info ? (
                    <div className="mt-4 text-sm text-[var(--jr-text-2)] flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> {i18n.t("invite.loading")}</div>
                ) : alreadyMember ? (
                    <>
                        <h1 className="font-display text-2xl tracking-tight mt-3">{i18n.t("invite.already_member")}</h1>
                        <p className="text-sm text-[var(--jr-text-2)] mt-2">
                            Dit account is al actief in <span className="text-white">{info.workspace_name}</span>. Log in om verder te gaan.
                        </p>
                        <button onClick={() => nav("/login")} data-testid="accept-goto-login"
                            className="mt-4 w-full px-5 py-2.5 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)]">
                            Naar login
                        </button>
                    </>
                ) : (
                    <>
                        <h1 className="font-display text-3xl tracking-tight mt-3">{i18n.t("invite.welcome_to", { name: info.workspace_name })}</h1>
                        <p className="text-sm text-[var(--jr-text-2)] mt-2">
                            <span className="text-white">{info.invited_by}</span> {i18n.t("invite.invited_you_as")} <span className="text-white">{info.role}</span>.
                        </p>

                        {isExisting && (
                            <div className="mt-4 rounded-lg border border-[var(--jr-info)]/30 bg-[var(--jr-info)]/[0.08] p-3 flex items-start gap-2"
                                data-testid="existing-user-banner">
                                <UserCheck size={14} className="text-[var(--jr-info)] mt-0.5 shrink-0" />
                                <div className="text-xs text-[var(--jr-text-2)]">
                                    <span className="text-white">{i18n.t("invite.has_account")}</span> {i18n.t("invite.confirm_pw_pre")} <span className="text-white">{info.workspace_name}</span> {i18n.t("invite.confirm_pw_post")}
                                </div>
                            </div>
                        )}

                        <form onSubmit={submit} className="mt-5 space-y-3">
                            <Field label={i18n.t("search.th_name")} value={name} onChange={setName}
                                placeholder={info.name || i18n.t("invite.your_name")} required
                                readOnly={isExisting} testid="accept-name" />
                            <Field label="E-mail" value={info.email} readOnly testid="accept-email" />
                            <Field
                                label={isExisting ? i18n.t("invite.lbl_existing_pw") : i18n.t("invite.lbl_pw")}
                                type="password" value={password} onChange={setPassword}
                                placeholder={isExisting ? "" : i18n.t("invite.min_chars")}
                                required testid="accept-password" />
                            {!isExisting && (
                                <Field label={i18n.t("invite.lbl_confirm_pw")} type="password" value={confirm} onChange={setConfirm} required testid="accept-confirm" />
                            )}
                            <button type="submit" disabled={saving} data-testid="accept-submit"
                                className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full bg-[var(--jr-brand)] text-white text-sm font-medium hover:bg-[var(--jr-brand-2)] disabled:opacity-60">
                                {saving ? <><Loader2 className="animate-spin" size={14} /> {isExisting ? i18n.t("invite.adding") : i18n.t("invite.creating_account")}</> : (isExisting ? i18n.t("invite.add_ws") : i18n.t("invite.join"))}
                            </button>
                            {isExisting && (
                                <p className="text-[10px] text-[var(--jr-text-3)] text-center flex items-center justify-center gap-1">
                                    <Info size={10} /> Je blijft hetzelfde wachtwoord behouden voor al je workspaces.
                                </p>
                            )}
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}

function Field({ label, value, onChange, placeholder, type = "text", required, readOnly, testid }) {
    return (
        <div>
            <label className="text-[10px] font-mono uppercase tracking-[0.18em] text-[var(--jr-text-3)]">{label}{required && " *"}</label>
            <input value={value} onChange={(e) => onChange?.(e.target.value)} type={type} required={required}
                placeholder={placeholder} readOnly={readOnly} data-testid={testid}
                className={`mt-1 w-full bg-[var(--jr-surface-2)] border border-white/10 rounded-lg px-3 py-2 text-sm focus:border-[var(--jr-brand)] outline-none ${readOnly ? "text-[var(--jr-text-2)]" : "text-white"}`} />
        </div>
    );
}
